/**
 * KCIS™ Pro — Audit trail drawer (Sprint 6B4).
 *
 * Read-only paginated timeline of every Sprint 4 mutation. Backed by
 * GET /api/nutrient/{aid}/audit-trail. Append-only by construction — the
 * UI never offers edit or delete affordances.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, History, Loader2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const ACTION_LABELS = {
  "ai_summary.regenerate": "AI summary regenerated",
  "ai_summary.block.edit": "AI block overridden",
  "override.pathway": "Pathway overridden",
  "escalation.suppress": "Escalation suppressed",
  "escalation.elevate": "Escalation elevated",
  "note.append": "Note appended",
  "review.mark.reviewed": "Marked reviewed",
  "review.mark.approved": "Marked approved",
  "review.mark.requires_follow_up": "Marked requires follow-up",
};

function fmtValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v.length > 140 ? `${v.slice(0, 140)}…` : v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

export default function AuditTrailDrawer({ assessmentId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const load = useCallback(
    async (p = 1) => {
      setLoading(true);
      try {
        const r = await api.get(`/nutrient/${assessmentId}/audit-trail`, {
          params: { page: p, page_size: PAGE_SIZE },
        });
        setItems(r?.data?.items || []);
        setTotal(r?.data?.total || 0);
        setPage(p);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [assessmentId]
  );

  useEffect(() => {
    if (open && items.length === 0 && !loading) {
      load(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid="audit-trail-drawer" className="rounded-2xl border border-slate-200 bg-white">
      <button
        type="button"
        data-testid="audit-trail-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <History className="w-5 h-5 text-slate-700" />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900">Immutable audit trail</div>
          <div className="text-xs text-slate-500">
            Every override, regenerate, and review-state change · append-only
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>

      {open ? (
        <div className="border-t border-slate-200">
          <div className="flex items-center justify-between px-4 py-2 bg-slate-50">
            <div className="text-xs text-slate-600" data-testid="audit-trail-count">
              {total} entr{total === 1 ? "y" : "ies"}
            </div>
            <Button
              size="sm"
              variant="ghost"
              data-testid="audit-trail-refresh"
              onClick={() => load(page)}
              disabled={loading}
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>

          {loading && items.length === 0 ? (
            <div className="px-4 py-6 flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading audit log…
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-6 text-sm text-slate-500" data-testid="audit-trail-empty">
              No audit entries yet.
            </div>
          ) : (
            <ol className="divide-y divide-slate-100">
              {items.map((it) => (
                <li
                  key={it.id || `${it.action}-${it.at}`}
                  data-testid={`audit-entry-${it.action}`}
                  className="px-4 py-3"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-medium text-slate-900">
                      {ACTION_LABELS[it.action] || it.action}
                      {it.target ? (
                        <span className="ml-2 text-xs text-slate-500">· {it.target}</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-slate-500 shrink-0">
                      {it.at ? new Date(it.at).toLocaleString() : ""}
                    </div>
                  </div>
                  <div className="text-xs text-slate-600 mt-1">
                    {it.actor_role || "—"} · {it.actor_id || "—"}
                  </div>
                  {it.reason ? (
                    <div className="text-xs text-slate-700 mt-1 italic">“{it.reason}”</div>
                  ) : null}
                  {(it.before !== null && it.before !== undefined) || (it.after !== null && it.after !== undefined) ? (
                    <div className="mt-2 grid sm:grid-cols-2 gap-2 text-xs">
                      <div className="rounded-md bg-slate-50 border border-slate-200 px-2 py-1">
                        <div className="text-[10px] uppercase tracking-wide text-slate-500">before</div>
                        <div className="text-slate-700 break-words">{fmtValue(it.before)}</div>
                      </div>
                      <div className="rounded-md bg-purple-50 border border-purple-200 px-2 py-1">
                        <div className="text-[10px] uppercase tracking-wide text-purple-700">after</div>
                        <div className="text-slate-800 break-words">{fmtValue(it.after)}</div>
                      </div>
                    </div>
                  ) : null}
                </li>
              ))}
            </ol>
          )}

          {totalPages > 1 ? (
            <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-t border-slate-200">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1 || loading}
                onClick={() => load(page - 1)}
                data-testid="audit-trail-prev"
              >
                Prev
              </Button>
              <div className="text-xs text-slate-600">
                Page {page} of {totalPages}
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages || loading}
                onClick={() => load(page + 1)}
                data-testid="audit-trail-next"
              >
                Next
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
